import type { RouteRecordRaw } from 'vue-router';

import { useFrameworkStore } from '@/stores/FrameworkStore.ts';
import { useOrganizationRisks } from '@/stores/v2/OrganizationRisks.ts';
import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';

import { useFlagsAccess } from '@/composables/Flags.ts';

import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { BreadcrumbFactory } from '../utils/breadcrumbFactory.ts';
import {
  validateConfigAccess,
  validateConfigUpdateAccess,
  validateGovernanceAccess,
  validateRiskManagementAccess,
  validateUserAccess,
} from '../utils/navigationGuard';

export const organizationRoutes: RouteRecordRaw[] = [
  {
    path: '/organization',
    name: 'organization',
    meta: {
      layout: DefaultLayout,
      breadcrumb: BreadcrumbFactory.static.organization(),
      sidebarSection: 'organization',
    },
    component: () => import('@/pages/Organization/OrganizationPage.vue'),

    children: [
      {
        path: '',
        name: 'organization-default',
        meta: {
          breadcrumb: BreadcrumbFactory.static.organizationFrameworks(),
        },
        beforeEnter: async () => {
          const organizationsStore = useOrganizationsStore();

          if (organizationsStore.riskManagementEnabled) {
            return { name: 'organization-risk-management' };
          } else if (organizationsStore.governanceEnabled) {
            return { name: 'organization-frameworks' };
          } else if (useFlagsAccess().haveUserAccess.value) {
            return { name: 'organization-users' };
          } else {
            return { name: 'organization-usage' };
          }
        },
        component: () => import('@/pages/Organization/Frameworks.vue'),
      },
      {
        path: 'risk-management',
        name: 'organization-risk-management',
        meta: { breadcrumb: BreadcrumbFactory.static.organizationRiskManagement() },
        component: () => import('@/pages/Organization/Risks/Risks.vue'),
        beforeEnter: (to, from, next) => {
          validateRiskManagementAccess(next);
        },
        children: [
          {
            path: 'categories/:categoryId',
            name: 'organization-taxonomy-category-detail',
            component: () => import('@/pages/Organization/Risks/CategoryDetail/CategoryDetail.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.organizationTaxonomyCategory(() =>
                useOrganizationRisks(),
              ),
            },
          },
          {
            path: 'risks/:riskId',
            name: 'organization-taxonomy-risk-detail',
            component: () => import('@/pages/Organization/Risks/RiskDetail/RiskDetail.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.organizationTaxonomyRisk(() => useOrganizationRisks()),
            },
          },
          {
            path: 'threats/:threatId',
            name: 'organization-taxonomy-threat-detail',
            component: () => import('@/pages/Organization/Risks/ThreatDetail/ThreatDetail.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.organizationTaxonomyThreat(() =>
                useOrganizationRisks(),
              ),
            },
          },
        ],
      },
      {
        path: 'users',
        name: 'organization-users',
        component: () => import('@/pages/Organization/Users.vue'),
        meta: { breadcrumb: BreadcrumbFactory.static.organizationUsers() },
        beforeEnter: async (to, from, next) => {
          return validateUserAccess(next);
        },
      },
      {
        path: 'frameworks',
        name: 'organization-frameworks',
        meta: { breadcrumb: BreadcrumbFactory.static.organizationFrameworks() },
        component: () => import('@/pages/Organization/Frameworks.vue'),
        beforeEnter: (to, from, next) => {
          validateGovernanceAccess(next);
        },
      },
      {
        path: 'frameworks/:frameworkCode',
        name: 'organization-framework-detail',
        meta: {
          breadcrumb: BreadcrumbFactory.framework(() => useFrameworkStore()),
        },
        component: () => import('@/pages/Organization/FrameworkPage.vue'),
        beforeEnter: (to, from, next) => {
          validateGovernanceAccess(next);
        },
      },
      {
        path: 'source-connectors',
        name: 'organization-source-connectors',
        meta: { breadcrumb: BreadcrumbFactory.static.sourceConnectors() },
        component: () => import('@/pages/Organization/SourceConnectors/SourceConnectors.vue'),
        beforeEnter: async (to, from, next) => {
          return validateConfigUpdateAccess(next);
        },
      },
      // Requested to hide in ticket #6539
      // {
      //   path: 'customization',
      //   name: 'organization-customization',
      //   meta: { breadcrumb: BreadcrumbFactory.static.organizationCustomization() },
      //   component: () => import('@/pages/Organization/Taxonomy.vue'),
      // },
      {
        path: 'notifications',
        name: 'organization-notifications',
        meta: { breadcrumb: BreadcrumbFactory.static.organizationNotifications() },
        component: () =>
          import('@/pages/Organization/NotificationsCenter/NotificationsCenterPage.vue'),
        beforeEnter: async (to, from, next) => {
          return validateConfigAccess(next);
        },
      },
      {
        // Every user of the organization reaches it, whatever their role: blocked actions redirect here.
        path: 'usage',
        name: 'organization-usage',
        meta: { breadcrumb: BreadcrumbFactory.static.organizationUsage() },
        component: () => import('@/pages/Organization/Usage/UsagePage.vue'),
      },
      {
        path: 'settings',
        name: 'organization-settings',
        meta: { breadcrumb: BreadcrumbFactory.static.organizationSettings() },
        component: () => import('@/pages/Organization/Settings/Settings.vue'),
        beforeEnter: async (to, from, next) => {
          return validateConfigAccess(next);
        },
      },
    ],
  },
];
